import { useState } from 'react';
import type { Character, CrawlerType } from '@dcc/rules/domain/character.ts';
import type { StatBlock, StatId } from '@dcc/rules/domain/stats.ts';
import { STAT_IDS, STAT_LABELS } from '@dcc/rules/domain/stats.ts';
import { STANDARD_ARRAY, rollStartingStats, validateStandardArray } from '@dcc/rules/rules/characterCreation.ts';
import { statModForScore } from '@dcc/rules/rules/statModifiers.ts';
import { randomRoller } from '@dcc/rules/rules/dice.ts';
import { legalSizesFor, sizeName } from '@dcc/rules/rules/size.ts';
import { startingAiFavor } from '@dcc/rules/rules/aiFavor.ts';
import type { BackgroundSelection } from '@dcc/rules/rules/backgrounds.ts';
import type { CombatSelection } from '@dcc/rules/rules/startingCombat.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { buildCharacter, validateDraft, type CreationDraft } from '@dcc/rules/app/creationService.ts';
import { BackgroundStep } from './BackgroundStep.tsx';
import { CombatStep } from './CombatStep.tsx';
import {
  CREATOR_STEPS, STEP_LABELS, canAdvance, chosenStats, nextStep, previousStep,
  type CreatorStep, type StatMethod,
} from './creatorModel.ts';

/**
 * Guided, rules-legal crawler creation, ported from the desktop app. Every
 * step can be revisited before the crawler is created. Legality comes from
 * the shared engine (validateDraft / buildCharacter), never from this screen.
 *
 * `onCreate` receives the finished crawler and saves it; it returns an error
 * message when saving failed, so the player keeps their choices and can retry.
 */
export function CreatorScreen(props: {
  newId: () => string;
  onCreate: (character: Character) => Promise<string | void>;
  onCancel: () => void;
}) {
  const [step, setStep] = useState<CreatorStep>('identity');
  const [name, setName] = useState('');
  const [crawlerType, setCrawlerType] = useState<CrawlerType>('human');
  const [size, setSize] = useState(4);
  const [method, setMethod] = useState<StatMethod>('array');
  const [assignment, setAssignment] = useState<Partial<Record<StatId, number>>>({});
  const [rolled, setRolled] = useState<StatBlock | null>(null);
  const [backgrounds, setBackgrounds] = useState<BackgroundSelection[]>([]);
  const [combat, setCombat] = useState<CombatSelection>({ second: null });
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const arrayReport = validateStandardArray(assignment);
  const stats = chosenStats(method, assignment, rolled);
  const tables = contentRegistry.tablesFor(crawlerType);
  const takenSkillIds = new Set(backgrounds.flatMap((b) => b.skillIds));

  const draft: CreationDraft | null = stats
    ? { id: 'draft', name: name.trim(), crawlerType, size, stats, backgrounds, combat, now: new Date().toISOString() }
    : null;
  const validation = draft ? validateDraft(draft, contentRegistry) : null;

  function updateBackground(selection: BackgroundSelection) {
    setBackgrounds((current) => [...current.filter((s) => s.tableId !== selection.tableId), selection]);
  }

  function changeCrawlerType(next: CrawlerType) {
    setCrawlerType(next);
    const legal = legalSizesFor(next);
    setSize(legal.includes(size) ? size : (legal[0] as number));
    // Background tables differ per crawler type, so earlier picks no longer apply.
    setBackgrounds([]);
    setCombat({ second: null });
  }

  async function finish() {
    if (!draft || !validation?.valid || saving) return;
    setError(null);
    setSaving(true);
    try {
      const built = buildCharacter({ ...draft, id: props.newId(), now: new Date().toISOString() }, contentRegistry);
      const problem = await props.onCreate(built.character);
      if (typeof problem === 'string') setError(problem);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'The crawler could not be created.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="dcc-creator">
      <h1>Create a crawler</h1>
      <ol className="dcc-creator-steps">
        {CREATOR_STEPS.map((s) => (
          <li key={s} className={s === step ? 'is-current' : ''}>{STEP_LABELS[s]}</li>
        ))}
      </ol>

      {error && <p className="dcc-save-status dcc-save-warn" role="alert">{error}</p>}

      {step === 'identity' && (
        <section className="dcc-creator-card">
          <h2>Identity</h2>
          <label className="dcc-creator-field">
            <span>Name</span>
            <input value={name} maxLength={60} onChange={(e) => setName(e.currentTarget.value)} />
          </label>
          <label className="dcc-creator-field">
            <span>Crawler type</span>
            <select value={crawlerType} onChange={(e) => changeCrawlerType(e.currentTarget.value as CrawlerType)}>
              <option value="human">Human</option>
              <option value="animal">Animal</option>
            </select>
          </label>
          <p className="dcc-creator-help">Changing the type resets background and combat choices, because the tables differ.</p>
          <label className="dcc-creator-field">
            <span>Size</span>
            <select value={size} disabled={crawlerType === 'human'} onChange={(e) => setSize(Number(e.currentTarget.value))}>
              {legalSizesFor(crawlerType).map((s) => (
                <option key={s} value={s}>{sizeName(s)} ({s})</option>
              ))}
            </select>
          </label>
          <p className="dcc-creator-help">Starting AI Favor: {startingAiFavor(crawlerType)}</p>
        </section>
      )}

      {step === 'stats' && (
        <section className="dcc-creator-card">
          <h2>Stats</h2>
          <div className="dcc-creator-modes">
            <button className={method === 'array' ? 'dcc-creator-option is-selected' : 'dcc-creator-option'} onClick={() => setMethod('array')}>
              Standard Array
            </button>
            <button className={method === 'roll' ? 'dcc-creator-option is-selected' : 'dcc-creator-option'} onClick={() => setMethod('roll')}>
              Leave it to Chance
            </button>
          </div>
          <p className="dcc-creator-help">Crawlers &amp; Customization pp.13-14</p>

          {method === 'array' ? (
            <>
              <p className="dcc-creator-help">Assign 2, 3, 4, 5 and 6, each score exactly once.</p>
              <div className="dcc-creator-stats">
                {STAT_IDS.map((stat) => (
                  <label key={stat} className="dcc-creator-stat">
                    <span>{STAT_LABELS[stat].abbr}</span>
                    <select
                      value={assignment[stat] ?? ''}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        setAssignment({ ...assignment, [stat]: value === '' ? undefined : Number(value) });
                      }}
                    >
                      <option value="">-</option>
                      {STANDARD_ARRAY.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                  </label>
                ))}
              </div>
              {!arrayReport.valid && (
                <ul className="dcc-creator-problems">
                  {arrayReport.errors.map((e) => <li key={e}>{e}</li>)}
                </ul>
              )}
            </>
          ) : (
            <>
              <p className="dcc-creator-help">
                Rolls 1d6 per Stat in order (Str, Int, Con, Dex, Cha), rerolling any 1s. Scores cannot be swapped afterwards.
              </p>
              <button onClick={() => setRolled(rollStartingStats(randomRoller))}>{rolled ? 'Roll again' : 'Roll Stats'}</button>
              {rolled && (
                <div className="dcc-creator-stats">
                  {STAT_IDS.map((stat) => (
                    <div key={stat} className="dcc-creator-stat">
                      <span>{STAT_LABELS[stat].abbr}</span>
                      <strong>{rolled[stat]}</strong>
                      <span className="dcc-creator-meta">+{statModForScore(rolled[stat])}</span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
          {stats && stats.int < contentRegistry.startingCombat.attackSpellMinimumIntelligence && (
            <p className="dcc-creator-help">
              Intelligence {stats.int} is below {contentRegistry.startingCombat.attackSpellMinimumIntelligence}, so a starting
              Attack Spell will not be available (C&amp;C p.13).
            </p>
          )}
        </section>
      )}

      {step === 'backgrounds' &&
        tables.map((table) => (
          <BackgroundStep key={table.id} table={table} selections={backgrounds} onChange={updateBackground} />
        ))}

      {step === 'combat' && stats && (
        <CombatStep
          crawlerType={crawlerType}
          intelligence={stats.int}
          takenSkillIds={takenSkillIds}
          selection={combat}
          onChange={setCombat}
          onBackToStats={() => setStep('stats')}
        />
      )}

      {step === 'review' && stats && draft && (
        <section className="dcc-creator-card">
          <h2>Review</h2>
          <p><strong>{name.trim() || '(unnamed)'}</strong>: {crawlerType}, {sizeName(size)}, Level 1, Floor 1</p>
          <div className="dcc-creator-stats">
            {STAT_IDS.map((stat) => (
              <div key={stat} className="dcc-creator-stat">
                <span>{STAT_LABELS[stat].abbr}</span>
                <strong>{stats[stat]}</strong>
                <span className="dcc-creator-meta">+{statModForScore(stats[stat])}</span>
              </div>
            ))}
          </div>
          {validation && !validation.valid ? (
            <div className="dcc-creator-warn">
              <p>This crawler is not rules-legal yet:</p>
              <ul className="dcc-creator-problems">
                {validation.problems.map((p, i) => (
                  <li key={i}>
                    {p.message}{' '}
                    <button className="dcc-creator-link" onClick={() => setStep(p.step)}>Fix in {STEP_LABELS[p.step]}</button>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <StartingSummary draft={draft} />
          )}
        </section>
      )}

      <div className="dcc-creator-nav">
        <button className="dcc-creator-secondary" onClick={props.onCancel}>Cancel</button>
        <span className="dcc-creator-spacer" />
        <button className="dcc-creator-secondary" disabled={step === 'identity'} onClick={() => setStep(previousStep(step))}>Back</button>
        {step !== 'review' ? (
          <button disabled={!canAdvance(step, name, stats)} onClick={() => setStep(nextStep(step))}>Next</button>
        ) : (
          <button disabled={!validation?.valid || saving} onClick={() => void finish()}>
            {saving ? 'Creating…' : 'Create crawler'}
          </button>
        )}
      </div>
    </div>
  );
}

function StartingSummary({ draft }: { draft: CreationDraft }) {
  const built = buildCharacter({ ...draft, id: 'preview' }, contentRegistry);
  return (
    <>
      <h3>Starting Skills</h3>
      <ul className="dcc-skill-list">
        {built.character.skills.map((s) => {
          const skill = contentRegistry.getSkill(s.skillId);
          return (
            <li key={s.skillId} className="dcc-skill">
              <span>{skill ? skill.name : s.skillId}</span>
              <span className="dcc-skill-rank">
                Rank {s.rank}{skill?.commonStat ? ` · ${STAT_LABELS[skill.commonStat].abbr}` : ''}
              </span>
            </li>
          );
        })}
      </ul>
      {built.startingItems.length > 0 && (
        <p className="dcc-creator-help">
          Starting equipment:{' '}
          {built.startingItems
            .map((grant) => `${grant.quantity} ${contentRegistry.getItem(grant.itemId)?.name ?? grant.itemId}`)
            .join('; ')}
        </p>
      )}
    </>
  );
}
